import React from 'react';

import { Box, Flex } from '@lightspeed/cirrus-core';
import Group from '@lightspeed/cirrus-group';
import Input from '@lightspeed/cirrus-input';
import Modal, { ModalFooter, ModalHeader } from '@lightspeed/cirrus-modal';
import Select from '@lightspeed/cirrus-select';
import Text from '@lightspeed/cirrus-text';
import { Button } from '@lightspeed/flame/Button';
import { IconChevronLeft } from '@lightspeed/flame/Icon/ChevronLeft';
import get from 'lodash/get';
import moment from 'moment';

import { DB_DATE_FORMAT } from 'pages/utils/time';

import { modalSteps } from '..';

export const manualDocumentTypes = {
  Passport: 'PASSPORT',
  'Military ID': 'MILITARY',
  'Native American Tribal ID': 'ABORIGINAL',
  'Citizenship card': 'CITIZENSHIP',
  // eslint-disable-next-line prettier/prettier
  "Driver's license": 'DriversLicense',
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'UPDATE_DOCUMENT_TYPE': {
      return { ...state, documentType: action.payload };
    }
    case 'UPDATE_DATE_OF_BIRTH': {
      return { ...state, dateOfBirth: action.payload };
    }
    case 'UPDATE_EXPIRY_DATE': {
      return { ...state, expiryDate: action.payload };
    }
    default: {
      return state;
    }
  }
};

const NOW = moment().format(DB_DATE_FORMAT);
const MIN_YEAR_OF_BIRTH = moment().subtract(120, 'years').year();
const MIN_EXPIRY_YEAR = moment().subtract(100, 'years').year();
const MAX_EXPIRY_YEAR = moment().add(100, 'years').year();

const AgeVerificationManual = ({ setStep, setStepWithData, reject, stepData }) => {
  const [isDateOfBirthError, setIsDateOfBirthError] = React.useState(false);
  const [isExpiryDateError, setIsExpiryDateError] = React.useState(false);
  const defaultDocumentType = 'Choose ID type';
  const previousDocumentType = get(stepData, 'documentType') || get(stepData, 'form.documentType');
  const dateOfBirth = get(stepData, 'dateOfBirth') || get(stepData, 'form.dateOfBirth');
  const expiryDate = get(stepData, 'cardExpiry') || get(stepData, 'form.cardExpiry');
  const [documentType] =
    Object.entries(manualDocumentTypes).find(([, value]) => value === previousDocumentType) || [];
  const [state, dispatch] = React.useReducer(reducer, {
    documentType: documentType || defaultDocumentType,
    dateOfBirth: dateOfBirth || '',
    expiryDate: expiryDate || '',
  });
  const countryCode = get(window, 'merchantos.locale.country_code', '').toLowerCase();

  const handleFieldChange = (field) => (e) => {
    dispatch({ type: `UPDATE_${field}`, payload: e.target.value });
  };

  const isManualVerificationValid =
    Object.values(state).every(Boolean) &&
    state.documentType !== defaultDocumentType &&
    !isDateOfBirthError &&
    !isExpiryDateError;

  return (
    <Modal className="cirrus responsive" isOpen onRequestClose={reject} css={{ width: '600px' }}>
      <ModalHeader onCloseClicked={reject}>Manager verification</ModalHeader>
      <div>
        <Box m="24px">
          <Text bodySmall bold>
            ID type
          </Text>
          <Select value={state.documentType} onChange={handleFieldChange('DOCUMENT_TYPE')}>
            <option hidden>Choose ID type</option>
            {Object.keys(manualDocumentTypes).map((type) => {
              if (type === 'Native American Tribal ID' && countryCode !== 'us') return null;
              return (
                <option key={type} value={type}>
                  {type}
                </option>
              );
            })}
          </Select>
        </Box>
        <Box m="24px" mt={0}>
          <Flex>
            <Box width="50%" mr={2}>
              <Flex flexDirection="column">
                <Text bodySmall bold>
                  Date of birth
                </Text>
                <Input
                  value={state.dateOfBirth}
                  onChange={(e) => {
                    handleFieldChange('DATE_OF_BIRTH')(e);
                    if (isDateOfBirthError) {
                      setIsDateOfBirthError(false);
                    }
                  }}
                  pattern="[0-9]{2}-[0-9]{2}-[0-9]{4}"
                  min={`${MIN_YEAR_OF_BIRTH}-01-01`}
                  max={NOW}
                  type="date"
                  onBlur={(e) => {
                    const { value } = e.target;
                    const currentYear = new Date(value).getFullYear();

                    if (currentYear < MIN_YEAR_OF_BIRTH || currentYear > moment(NOW).year()) {
                      setIsDateOfBirthError(true);
                    }
                  }}
                />
                {isDateOfBirthError ? (
                  <Text bodySmall color="maple-200">
                    Date of birth must be between {MIN_YEAR_OF_BIRTH}-01-01 and {NOW}
                  </Text>
                ) : null}
              </Flex>
            </Box>
            <Box width="50%">
              <Flex flexDirection="column">
                <Text bodySmall bold>
                  ID expiry date
                </Text>
                <Input
                  value={state.expiryDate}
                  onChange={(e) => {
                    handleFieldChange('EXPIRY_DATE')(e);
                    if (isExpiryDateError) {
                      setIsExpiryDateError(false);
                    }
                  }}
                  pattern="[0-9]{2}-[0-9]{2}-[0-9]{4}"
                  min={`${MIN_EXPIRY_YEAR}-01-01`}
                  max={`${MAX_EXPIRY_YEAR}-12-31`}
                  type="date"
                  onBlur={(e) => {
                    const { value } = e.target;
                    const currentYear = new Date(value).getFullYear();

                    if (currentYear < MIN_EXPIRY_YEAR || currentYear > MAX_EXPIRY_YEAR) {
                      setIsExpiryDateError(true);
                    }
                  }}
                />
                {isExpiryDateError ? (
                  <Text bodySmall color="maple-200">
                    ID expiry date must be between {MIN_EXPIRY_YEAR}-01-01 and {MAX_EXPIRY_YEAR}
                    -12-31
                  </Text>
                ) : null}
              </Flex>
            </Box>
          </Flex>
        </Box>
      </div>
      <ModalFooter>
        <Group className="cr-justify-between">
          <Button onClick={() => setStep(modalSteps.AGE_VERIFICATION)}>
            <IconChevronLeft />
            Back
          </Button>
          <Group className="cr-justify-end">
            <Button onClick={reject}>Cancel</Button>
            <Button
              disabled={!isManualVerificationValid}
              variant="primary"
              fill
              onClick={() => {
                setStepWithData({
                  step: modalSteps.AGE_VERIFICATION_PIN_OVERRIDE,
                  data: {
                    documentType: manualDocumentTypes[state.documentType],
                    dateOfBirth: state.dateOfBirth,
                    cardExpiry: state.expiryDate,
                  },
                });
              }}
            >
              Confirm
            </Button>
          </Group>
        </Group>
      </ModalFooter>
    </Modal>
  );
};

export default AgeVerificationManual;
